import {
  Document,
  Paragraph,
  AlignmentType,
  TextRun,
  convertInchesToTwip,
  Packer,
  BorderStyle,
  Header,
  Footer,
  PageNumber,
} from 'docx';
import { saveAs } from 'file-saver';
import type { Option, Section,  } from '../types';
import { Locale  } from '../types';

// Professional color scheme
const COLORS = {
  primary: '1E3A8A', // Deep blue
  secondary: '3B82F6', // Bright blue
  accent: '60A5FA', // Light blue
  text: '1E293B', // Dark text
  lightText: '64748B', // Gray text
  border: 'CBD5E1', // Light border
};

// Typography sizes (in half-points, so 24 = 12pt)
const FONT_SIZES = {
  title: 48,        // 24pt
  heading1: 32,     // 16pt
  heading2: 28,     // 14pt
  subheading: 24,   // 12pt
  body: 22,         // 11pt
  small: 20,        // 10pt
};

// Spacing values (in twips, 1 inch = 1440 twips)
const SPACING = {
  section: 480,     // ~0.33 inch
  paragraph: 240,   // ~0.17 inch
  line: 120,        // ~0.08 inch
  small: 60,        // ~0.04 inch
};


interface DocxGenerationData {
  companyName: string;
  sections: Section[];
  selectedOptions: Map<string, { option: Option; quantity: number }>;
  totalPrice: {
    us: number;
    eu: number;
    kzt: number;
  };
  projectDeadline: number;
  locale: Locale;
  currency: 'us' | 'eu' | 'kzt';
}

const getCurrencySymbol = (currency: 'us' | 'eu' | 'kzt'): string => {
  const symbols = {
    us: '$',
    eu: '€',
    kzt: '₸',
  };
  return symbols[currency];
};

const formatPrice = (price: number, currency: 'us' | 'eu' | 'kzt'): string => {
  const symbol = getCurrencySymbol(currency);
  const formatted = new Intl.NumberFormat('en-US').format(price);
  return currency === 'kzt' ? `${formatted} ${symbol}` : `${symbol}${formatted}`;
};

const getDocumentTitle = (locale: Locale): string => {
  const titles: Record<Locale, string> = {
    [Locale.en]: 'Commercial Proposal',
    [Locale.ru]: 'Коммерческое предложение',
    [Locale.kz]: 'Коммерциялық ұсыныс',
  };
  return titles[locale];
};

const getTranslations = (locale: Locale) => {
  const translations: Record<Locale, {
    company: string;
    date: string;
    projectSummary: string;
    totalCost: string;
    projectTimeline: string;
    days: string;
    selectedServices: string;
    section: string;
    service: string;
    description: string;
    quantity: string;
    unitPrice: string;
    totalPrice: string;
    daysToComplete: string;
    advantages: string;
    disadvantages: string;
    serviceDetails: string;
  }> = {
    [Locale.en]: {
      company: 'Company',
      date: 'Date',
      projectSummary: 'Project Summary',
      totalCost: 'Total Cost',
      projectTimeline: 'Project Timeline',
      days: 'days',
      selectedServices: 'Selected Services',
      section: 'Section',
      service: 'Service',
      description: 'Description',
      quantity: 'Quantity',
      unitPrice: 'Unit Price',
      totalPrice: 'Total Price',
      daysToComplete: 'Days to Complete',
      advantages: 'Advantages',
      disadvantages: 'Disadvantages',
      serviceDetails: 'Service Details',
    },
    [Locale.ru]: {
      company: 'Компания',
      date: 'Дата',
      projectSummary: 'Сводка проекта',
      totalCost: 'Общая стоимость',
      projectTimeline: 'Срок выполнения проекта',
      days: 'дней',
      selectedServices: 'Выбранные услуги',
      section: 'Раздел',
      service: 'Услуга',
      description: 'Описание',
      quantity: 'Количество',
      unitPrice: 'Цена за единицу',
      totalPrice: 'Общая цена',
      daysToComplete: 'Дней на выполнение',
      advantages: 'Преимущества',
      disadvantages: 'Недостатки',
      serviceDetails: 'Детали услуг',
    },
    [Locale.kz]: {
      company: 'Компания',
      date: 'Күні',
      projectSummary: 'Жоба қорытындысы',
      totalCost: 'Жалпы құны',
      projectTimeline: 'Жобаның орындалу мерзімі',
      days: 'күн',
      selectedServices: 'Таңдалған қызметтер',
      section: 'Бөлім',
      service: 'Қызмет',
      description: 'Сипаттама',
      quantity: 'Саны',
      unitPrice: 'Бірлік бағасы',
      totalPrice: 'Жалпы баға',
      daysToComplete: 'Орындау күндері',
      advantages: 'Артықшылықтар',
      disadvantages: 'Кемшіліктер',
      serviceDetails: 'Қызмет мәліметтері',
    },
  };
  return translations[locale];
};

export async function generateProjectDocx(data: DocxGenerationData): Promise<Blob> {
  const t = getTranslations(data.locale);
  const currentDate = new Date().toLocaleDateString();

  // Create header section with professional styling
  const headerSection: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: getDocumentTitle(data.locale),
          bold: true,
          size: FONT_SIZES.title,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        before: SPACING.section,
        after: SPACING.section
      },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '━'.repeat(50),
          color: COLORS.accent,
          size: FONT_SIZES.small,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: SPACING.paragraph },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${t.company}: `,
          bold: true,
          size: FONT_SIZES.body,
          color: COLORS.text,
          font: 'Calibri',
        }),
        new TextRun({
          text: data.companyName,
          size: FONT_SIZES.body,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: SPACING.line },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${t.date}: `,
          bold: true,
          size: FONT_SIZES.body,
          color: COLORS.text,
          font: 'Calibri',
        }),
        new TextRun({
          text: currentDate,
          size: FONT_SIZES.body,
          color: COLORS.lightText,
          font: 'Calibri',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: SPACING.section },
    }),
  ];

  // Create project summary section with enhanced styling
  const summarySection: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: t.projectSummary,
          bold: true,
          size: FONT_SIZES.heading1,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      spacing: {
        before: SPACING.section,
        after: SPACING.paragraph
      },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${t.totalCost}: `,
          bold: true,
          size: FONT_SIZES.subheading,
          color: COLORS.text,
          font: 'Calibri',
        }),
        new TextRun({
          text: formatPrice(data.totalPrice[data.currency], data.currency),
          size: FONT_SIZES.subheading,
          color: COLORS.secondary,
          bold: true,
          font: 'Calibri',
        }),
      ],
      // Use different spacing based on whether timeline will be added
      spacing: { after: data.projectDeadline > 0 ? SPACING.line : SPACING.section },
    }),
  ];

  // Only add project timeline if deadline is greater than 0
  if (data.projectDeadline > 0) {
    summarySection.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${t.projectTimeline}: `,
            bold: true,
            size: FONT_SIZES.subheading,
            color: COLORS.text,
            font: 'Calibri',
          }),
          new TextRun({
            text: `${data.projectDeadline} ${t.days}`,
            size: FONT_SIZES.subheading,
            color: COLORS.secondary,
            bold: true,
            font: 'Calibri',
          }),
        ],
        spacing: { after: SPACING.section },
      })
    );
  }

  // Create service details section with enhanced styling
  const serviceDetailsSection: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: t.serviceDetails,
          bold: true,
          size: FONT_SIZES.heading1,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      spacing: {
        before: SPACING.section,
        after: SPACING.paragraph
      },
    }),
  ];

  // Add detailed information for each selected option with enhanced styling
  data.selectedOptions.forEach(({ option, quantity }) => {
    const section = data.sections.find(s => s.id === option.section_id);
    const sectionTitle = section?.title[data.locale] || section?.title[Locale.en] || '';
    const optionTitle = option.title[data.locale] || option.title[Locale.en];
    const optionDescription = option.description[data.locale] || option.description[Locale.en];

    // Add separator line before each service
    serviceDetailsSection.push(
      new Paragraph({
        children: [
          new TextRun({
            text: '─'.repeat(80),
            color: COLORS.border,
            size: FONT_SIZES.small,
          }),
        ],
        spacing: { before: SPACING.paragraph, after: SPACING.paragraph },
      })
    );

    serviceDetailsSection.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${sectionTitle}: ${optionTitle}`,
            bold: true,
            size: FONT_SIZES.heading2,
            color: COLORS.secondary,
            font: 'Calibri',
          }),
        ],
        spacing: { before: SPACING.paragraph, after: SPACING.line },
      })
    );

    if (quantity > 1) {
      serviceDetailsSection.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${t.quantity}: `,
              bold: true,
              size: FONT_SIZES.body,
              color: COLORS.text,
              font: 'Calibri',
            }),
            new TextRun({
              text: quantity.toString(),
              size: FONT_SIZES.body,
              color: COLORS.secondary,
              font: 'Calibri',
            }),
          ],
          spacing: { after: SPACING.line },
        })
      );
    }

    serviceDetailsSection.push(
      new Paragraph({
        children: [
          new TextRun({
            text: optionDescription,
            size: FONT_SIZES.body,
            color: COLORS.text,
            font: 'Calibri',
          }),
        ],
        spacing: { after: SPACING.paragraph },
      })
    );

    // Add pros if available with better formatting
    const prosArray = option.pros?.[data.locale] || option.pros?.[Locale.en] || [];
    if (prosArray.length > 0) {
      serviceDetailsSection.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `✓ ${t.advantages}`,
              bold: true,
              size: FONT_SIZES.body,
              color: '10B981', // Green color for advantages
              font: 'Calibri',
            }),
          ],
          spacing: { before: SPACING.line, after: SPACING.small },
        })
      );

      prosArray.forEach((proText: string) => {
        serviceDetailsSection.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `• ${proText}`,
                size: FONT_SIZES.body,
                color: COLORS.text,
                font: 'Calibri',
              }),
            ],
            indent: { left: convertInchesToTwip(0.5) },
            spacing: { after: SPACING.small },
          })
        );
      });
    }

    // Add cons if available with better formatting
    const consArray = option.cons?.[data.locale] || option.cons?.[Locale.en] || [];
    if (consArray.length > 0) {
      serviceDetailsSection.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `✗ ${t.disadvantages}`,
              bold: true,
              size: FONT_SIZES.body,
              color: 'EF4444', // Red color for disadvantages
              font: 'Calibri',
            }),
          ],
          spacing: { before: SPACING.line, after: SPACING.small },
        })
      );

      consArray.forEach((conText: string) => {
        serviceDetailsSection.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `• ${conText}`,
                size: FONT_SIZES.body,
                color: COLORS.text,
                font: 'Calibri',
              }),
            ],
            indent: { left: convertInchesToTwip(0.5) },
            spacing: { after: SPACING.small },
          })
        );
      });
    }
  });

  // Add thank you note at the end
  const thankYouSection: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: '━'.repeat(50),
          color: COLORS.accent,
          size: FONT_SIZES.small,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: SPACING.section, after: SPACING.paragraph },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: data.locale === Locale.en
            ? 'Thank you for considering our services!'
            : data.locale === Locale.ru
            ? 'Спасибо за рассмотрение наших услуг!'
            : 'Біздің қызметтерімізді қарастырғаныңыз үшін рахмет!',
          italics: true,
          size: FONT_SIZES.subheading,
          color: COLORS.primary,
          font: 'Calibri',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: SPACING.paragraph, after: SPACING.paragraph },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: data.locale === Locale.en
            ? 'We look forward to working with you.'
            : data.locale === Locale.ru
            ? 'Мы с нетерпением ждем сотрудничества с вами.'
            : 'Сізбен жұмыс істеуді асыға күтеміз.',
          size: FONT_SIZES.body,
          color: COLORS.lightText,
          font: 'Calibri',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: SPACING.section },
    }),
  ];

  // Create professional headers and footers
  const documentHeader = new Header({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: `${data.companyName} | ${getDocumentTitle(data.locale)}`,
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
        ],
        alignment: AlignmentType.RIGHT,
        border: {
          bottom: {
            color: COLORS.border,
            space: 1,
            size: 6,
            style: BorderStyle.SINGLE,
          },
        },
      }),
    ],
  });

  const documentFooter = new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: `${currentDate} | `,
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
          new TextRun({
            text: data.locale === Locale.en
              ? 'Page '
              : data.locale === Locale.ru
              ? 'Страница '
              : 'Бет ',
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
          new TextRun({
            children: [PageNumber.CURRENT],
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
          new TextRun({
            text: data.locale === Locale.en
              ? ' of '
              : data.locale === Locale.ru
              ? ' из '
              : ' / ',
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
          new TextRun({
            children: [PageNumber.TOTAL_PAGES],
            size: FONT_SIZES.small,
            color: COLORS.lightText,
            font: 'Calibri',
          }),
        ],
        alignment: AlignmentType.CENTER,
        border: {
          top: {
            color: COLORS.border,
            space: 1,
            size: 6,
            style: BorderStyle.SINGLE,
          },
        },
      }),
    ],
  });

  // Create the document with enhanced layout
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
          titlePage: false,
        },
        headers: {
          default: documentHeader,
        },
        footers: {
          default: documentFooter,
        },
        children: [
          ...headerSection,
          ...summarySection,
          ...serviceDetailsSection,
          ...thankYouSection,
        ],
      },
    ],
  });

  // Generate and return the blob
  const blob = await Packer.toBlob(doc);
  return blob;
}

export function formatDocxFilename(companyName: string): string {
  const sanitized = companyName.replace(/[^a-zA-Z0-9]/g, '_');
  const date = new Date().toISOString().split('T')[0];
  return `${sanitized}_proposal_${date}.docx`;
}

export async function downloadProjectDocx(data: DocxGenerationData): Promise<void> {
  try {
    const blob = await generateProjectDocx(data);
    const filename = formatDocxFilename(data.companyName);
    saveAs(blob, filename);
  } catch (error) {
    console.error('Error generating DOCX:', error);
    throw error;
  }
}